import { Reference } from '@apollo/client';
import { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  CommunityFeedPostFragmentDoc,
  MediaInput,
  useCommunitiesV2AddPostMutation,
} from 'generated/graphql';
import { upload } from '@client/components/ajax';
import { pickObject } from '@client/new/helpers/general';
import { CommunityId } from '../model/community.types';
import { resolveInfluencerId } from '../lib/resolveInfluencerId';

const MEDIA_INPUT_KEYS = [
  'audio',
  'cdn01',
  'height',
  'image',
  'mimeType',
  'source',
  'video',
  'width',
] as const;

// A new post belongs on the first page of the main feed variants only. Skip
// cursor-paginated pages (they carry `lastId`/`direction`) and the pinned list
// (`type: pinned`) — a freshly created post is neither paginated nor pinned.
const isFirstPageVariant = (storeFieldName: string): boolean =>
  !storeFieldName.includes('lastId') &&
  !storeFieldName.includes('direction') &&
  !storeFieldName.includes('"type"');

type AddPostInput = { text: string; files: File[]; videoUuids: string[] };

export const useAddPost = (communityId: CommunityId, influencerId?: string) => {
  const [addPostMutation] = useCommunitiesV2AddPostMutation();
  const creatorId = resolveInfluencerId(influencerId);

  return useCallback(
    async ({ text, files, videoUuids }: AddPostInput): Promise<boolean> => {
      try {
        let mediaInput: MediaInput[] = [];
        if (files.length > 0 || videoUuids.length > 0) {
          if (creatorId === null) throw new Error('Missing creator id');
          const uploaded = await Promise.all(
            files.map((file) => upload(file, creatorId, {})),
          );
          const fileInputs = uploaded.map((item) =>
            pickObject(item, MEDIA_INPUT_KEYS),
          );
          // Video is already in the creator's Mux library; creator-api resolves
          // its real encoding state from the uuid and fills the playback media.
          const videoInputs: MediaInput[] = videoUuids.map((uuid) => ({
            uuid,
          }));
          mediaInput = [...fileInputs, ...videoInputs];
        }

        await addPostMutation({
          variables: { communityId, text, media: mediaInput },
          update: (cache, { data }) => {
            const created = data?.addPost;
            if (!created) return;

            const ref = cache.writeFragment({
              fragment: CommunityFeedPostFragmentDoc,
              fragmentName: 'CommunityFeedPost',
              data: created,
            });
            if (!ref) return;

            cache.modify({
              id: cache.identify({ __typename: 'Community', id: communityId }),
              fields: {
                posts: (
                  existing: readonly Reference[] = [],
                  { storeFieldName, readField },
                ) => {
                  if (!isFirstPageVariant(storeFieldName)) return existing;
                  const present = existing.some(
                    (postRef) => readField('id', postRef) === created.id,
                  );
                  return present ? existing : [ref, ...existing];
                },
              },
            });
          },
        });
        return true;
      } catch {
        toast.error('Something went wrong');
        return false;
      }
    },
    [addPostMutation, communityId, creatorId],
  );
};
